import { SectionContainer } from '@/pages/shared-components/SectionContainer'
import { Button, buttonVariants } from '@/components/shadcn/button'
import { IconBg } from '@/components/ui/IconBg'
import { NoSearchResultsDisplay } from '@/components/ui/NoSeachResultsDisplay'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { cn } from '@/lib/utils'
import { IconDatabricksOutline } from '@icons/IconDatabricksOutline'
import { IconEditOutline } from '@icons/IconEditOutline'
import { IconPlayOutline } from '@icons/IconPlayOutline'
import { IconTransformOutline } from '@icons/IconTransformOutline'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/shadcn/dialog'
import { Label } from '@/components/shadcn/label'
import { Textarea } from '@/components/shadcn/textarea'
import { getDatabricksErrorMessage } from '@/lib/databricksErrorMessage'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { useState } from 'react'
import { toast } from 'sonner'
import { useCurrentProject } from '../useCurrentProject'

type DatabricksProjectJob = RestSchemas['DatabricksMessages.DatabricksProjectJob']

export function ProjectDatabricksJobsSection() {
  const { projectId } = useCurrentProject()
  const { putDatabricksApiSyncProjectJobs, getDatabricksApiGetDatabricksProjectIntegrationStatus } = useRestClient()
  const { mutate: syncProjectJobs, isPending } = putDatabricksApiSyncProjectJobs.useMutation()

  const invalidateIntegration = () =>
    getDatabricksApiGetDatabricksProjectIntegrationStatus.invalidateQueries()

  const onSync = () => {
    syncProjectJobs(
      { query: { projectId } },
      {
        onError: (e) => toast.error('Error syncing jobs: ' + getDatabricksErrorMessage(e)),
        onSuccess: () => {
          toast.success('Jobs synced')
          invalidateIntegration()
        },
      }
    )
  }

  return (
    <SectionContainer
      title="Jobs"
      action={
        <TooltipButton tooltip="Sync jobs">
          <Button variant="ghost" size="icon" className={isPending ? 'animate-spin' : ''} disabled={isPending} onClick={onSync}>
            <IconTransformOutline className="size-5" />
          </Button>
        </TooltipButton>
      }
    >
      <ProjectDatabricksJobsSectionContent />
    </SectionContainer>
  )
}

function ProjectDatabricksJobsSectionContent() {
  const { projectId } = useCurrentProject()
  const { getDatabricksApiGetDatabricksProjectIntegrationStatus } = useRestClient()
  const { data: integration, isLoading } = getDatabricksApiGetDatabricksProjectIntegrationStatus.useQuery({
    query: { projectId },
  })
  const jobs = integration?.jobs ?? []

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading jobs...</div>
  }

  if (jobs.length === 0) {
    return <NoSearchResultsDisplay text="No jobs found for this project" className="pt-0" />
  }

  return (
    <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(350px,1fr))] gap-1">
      {jobs.map((job) => (
        <ProjectDatabricksJobCard key={job.jobId} job={job} />
      ))}
    </div>
  )
}

function ProjectDatabricksJobCard({ job }: { job: DatabricksProjectJob }) {
  const { projectId } = useCurrentProject()
  const { putDatabricksApiRunProjectJob, getDatabricksApiListProjectJobRuns } = useRestClient()
  const { mutate: runProjectJob, isPending } = putDatabricksApiRunProjectJob.useMutation()
  const invalidateRuns = () => getDatabricksApiListProjectJobRuns.invalidateQueries()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [contextJson, setContextJson] = useState('')

  if (!job.jobId) return null

  const onConfirmRun = () => {
    const context = contextJson.trim()
    // openapi-qraft sends string bodies as raw text, but ASP.NET [FromBody] string expects JSON (e.g. "{\"key\":\"value\"}").
    runProjectJob(
      {
        query: { projectId, jobId: job.jobId! },
        body: JSON.stringify(context || null),
      },
      {
        onError: (e) => toast.error('Error running job: ' + getDatabricksErrorMessage(e)),
        onSuccess: () => {
          toast.success('Job running')
          setDialogOpen(false)
          setContextJson('')
        },
        onSettled: () => invalidateRuns(),
      }
    )
  }

  return (
    <>
      <div className="grid grid-cols-[1fr_4fr_1fr] gap-2 rounded-lg bg-background p-4 shadow">
        <IconBg className="size-12">
          <IconDatabricksOutline className="size-6" />
        </IconBg>
        <div className="flex flex-col justify-between gap-1">
          <div className="text-base">{job.jobName}</div>
          <div className="text-xs font-light">{job.notebook}</div>
        </div>
        <div className="col-span-2 col-start-2 flex flex-row justify-end gap-2 border-t pt-3">
          <Button size="sm" className="h-6" onClick={() => setDialogOpen(true)}>
            <IconPlayOutline className="size-4" />
            <span className="ml-1 text-xs">Run</span>
          </Button>
          {job.jobUrl && (
            <a className={cn(buttonVariants({ size: 'sm', variant: 'outline' }), 'h-6 text-foreground')} href={job.jobUrl} target="_blank">
              <IconEditOutline className="size-4" />
              <span className="ml-1 text-xs">Edit Job</span>
            </a>
          )}
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Run {job.jobName}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor="context-input">Context (optional JSON)</Label>
            <Textarea id="context-input" placeholder='{"key": "value"}' value={contextJson} onChange={(e) => setContextJson(e.target.value)} rows={6} className="font-mono text-xs" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={onConfirmRun} disabled={isPending}>
              {isPending ? 'Starting...' : 'Run'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
